import React from 'react';
import { motion } from 'framer-motion';
import { 
  TruckIcon, 
  GlobeAltIcon, 
  HeartIcon, 
  ClockIcon,
  ShieldCheckIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const Features: React.FC = () => {
  const features = [
    {
      icon: GlobeAltIcon,
      title: '100% Organic',
      description: 'All our products are certified organic, grown without harmful pesticides or chemicals.',
      color: 'from-green-500 to-green-600'
    },
    {
      icon: TruckIcon,
      title: 'Daily Delivery',
      description: 'Fresh from our farm to your doorstep every morning, ensuring maximum freshness.',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: HeartIcon,
      title: 'Farm to Table',
      description: 'Direct from our sustainable farm practices to your family\'s dining table.',
      color: 'from-red-500 to-red-600'
    },
    {
      icon: ClockIcon,
      title: 'Always Fresh',
      description: 'Harvested at peak ripeness and delivered within hours for optimal taste and nutrition.',
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Quality Assured',
      description: 'Rigorous quality checks and food safety standards at every step of the process.',
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: UserGroupIcon,
      title: 'Community Focused',
      description: 'Supporting local communities and sustainable farming practices for a better future.',
      color: 'from-indigo-500 to-indigo-600'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <section id="services" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Why Choose AgriVikas?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We combine traditional farming wisdom with modern technology to deliver 
            the freshest, most nutritious products to your family.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="card p-8 text-center group"
            >
              <div className={`inline-flex p-4 rounded-full bg-gradient-to-r ${feature.color} mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className="h-8 w-8 text-white" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {feature.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
